import type { FilterConfig } from '../../../../../apis/graphql';
import { FilterOptionSource, FilterType } from '../../../../../apis/graphql';
import type { SearchParamsValuesType } from '../../../../../constants';
import { DEFAULT_RANGE_FILTER_VALUE } from '../../../../../contexts/FilterContext';
import type {
  AdditionalFilterBoolean,
  AdditionalFilterCalendar,
  AdditionalFilterDropdown,
  AdditionalFilterInDropdown,
  AdditionalFilterRange,
  BooleanFilterOptions,
  IncludeExcludeOptions,
  RangeOptions,
  UseOptions,
} from '../constants';
import { AdditionalFilterType, DEFAULT_BOOLEAN_OPTIONS } from '../constants';
import { useGetFilterOptions } from './useGetFilterOptions';

function mapBooleanFilterConfig(config: FilterConfig): AdditionalFilterBoolean {
  const options: BooleanFilterOptions =
    config.staticOptions?.length === 2
      ? { yes: config.staticOptions[0].label, no: config.staticOptions[1].label }
      : DEFAULT_BOOLEAN_OPTIONS;

  return {
    type: AdditionalFilterType.BOOLEAN,
    label: config.label,
    searchParam: config.id as SearchParamsValuesType,
    value: null,
    options,
  };
}

function mapStaticDropdownConfig(config: FilterConfig): AdditionalFilterDropdown {
  const staticOpts = (config.staticOptions ?? []).map((o) => ({ label: o.label, value: o.value }));
  const filterType =
    config.type === FilterType.Dropdown ? AdditionalFilterType.DROPDOWN : getCheckboxOrRadioType(config);

  return {
    type: filterType,
    label: config.label,
    multiOptionalLabel: config.multiOptionalLabel ?? `All ${config.label}s`,
    searchParam: config.id as SearchParamsValuesType,
    value: [],
    useOptions: (): UseOptions => ({ options: staticOpts }),
    isSearchable: config.isSearchable ?? undefined,
    ...(config.includeExclude ? { includeExcludeOptions: mapIncludeExclude(config) } : {}),
  };
}

function mapDynamicDropdownConfig(config: FilterConfig): AdditionalFilterDropdown {
  const filterType =
    config.type === FilterType.Dropdown ? AdditionalFilterType.DROPDOWN : getCheckboxOrRadioType(config);
  const isSearchable = config.isSearchable ?? filterType === AdditionalFilterType.DROPDOWN;

  return {
    type: filterType,
    label: config.label,
    multiOptionalLabel: config.multiOptionalLabel ?? `All ${config.label}s`,
    searchParam: config.id as SearchParamsValuesType,
    value: [],
    useOptions: (): UseOptions => useGetFilterOptions(config.id, isSearchable),
    isSearchable: isSearchable || undefined,
    ...(config.includeExclude ? { includeExcludeOptions: mapIncludeExclude(config) } : {}),
  };
}

function getCheckboxOrRadioType(
  config: FilterConfig
): AdditionalFilterType.CHECKBOX | AdditionalFilterType.RADIOBUTTON | AdditionalFilterType.DROPDOWN {
  if (config.type === FilterType.Checkbox) return AdditionalFilterType.CHECKBOX;
  if (config.type === FilterType.Radiobutton) return AdditionalFilterType.RADIOBUTTON;
  return AdditionalFilterType.DROPDOWN;
}

function isRangeOrCalendarConfig(config: FilterConfig): boolean {
  if (config.isBoolean) return false;
  return (
    config.type === FilterType.Range ||
    config.type === FilterType.Comparison ||
    config.type === FilterType.OperatorValueRange ||
    config.type === FilterType.Calendar
  );
}

function isCalendarConfig(config: FilterConfig): boolean {
  if (config.isBoolean) return false;
  return config.type === FilterType.Calendar;
}

function mapRangeFilterConfig(config: FilterConfig): AdditionalFilterRange {
  const displayMultiplier = config.rangeConfig?.displayMultiplier;
  const hasRangeOptions = config.valuePrefix || config.valueSuffix || displayMultiplier || config.valueDecimals != null;
  // KATANA-1522: derive currency converters from displayMultiplier so entered values round-trip.
  const converters: Pick<RangeOptions, 'convertToDisplayValue' | 'convertToSaveValue'> =
    displayMultiplier != null
      ? {
          convertToDisplayValue: (value) => (value !== null ? value * displayMultiplier : null),
          convertToSaveValue: (value) => (value !== null ? value / displayMultiplier : null),
        }
      : {};
  return {
    type: AdditionalFilterType.RANGE,
    label: config.label,
    searchParam: config.id as SearchParamsValuesType,
    value: DEFAULT_RANGE_FILTER_VALUE,
    ...(hasRangeOptions
      ? {
          rangeOptions: {
            valuePrefix: config.valuePrefix,
            valueSuffix: config.valueSuffix,
            displayMultiplier,
            valueDecimals: config.valueDecimals,
            ...converters,
          },
        }
      : {}),
  };
}

function mapCalendarFilterConfig(config: FilterConfig): AdditionalFilterCalendar {
  return {
    type: AdditionalFilterType.CALENDAR,
    label: config.label,
    searchParam: config.id as SearchParamsValuesType,
    value: null,
  };
}

function mapIncludeExclude(config: FilterConfig): IncludeExcludeOptions | undefined {
  if (!config.includeExclude) return undefined;
  return {
    include: config.includeExclude.includeKey as SearchParamsValuesType,
    exclude: config.includeExclude.excludeKey as SearchParamsValuesType,
  };
}

export function isBooleanConfig(config: FilterConfig): boolean {
  return !!config.isBoolean;
}

export function isDropdownOrCheckboxConfig(config: FilterConfig): boolean {
  if (config.isBoolean) return false;
  return (
    config.type === FilterType.Dropdown ||
    config.type === FilterType.Checkbox ||
    config.type === FilterType.Radiobutton ||
    config.type === FilterType.SearchableMultiSelect
  );
}

export function isBooleanOrStaticRadioConfig(config: FilterConfig): boolean {
  if (config.isBoolean) return true;
  if (
    config.type === FilterType.Radiobutton &&
    config.optionSource === FilterOptionSource.Static &&
    !config.isBoolean
  ) {
    return true;
  }
  return false;
}

function isConfigMappable(config: FilterConfig): boolean {
  return isBooleanConfig(config) || isDropdownOrCheckboxConfig(config) || isRangeOrCalendarConfig(config);
}

function mapSingleFilterConfig(config: FilterConfig): AdditionalFilterInDropdown {
  if (config.isBoolean) return mapBooleanFilterConfig(config);

  if (isCalendarConfig(config)) return mapCalendarFilterConfig(config);

  if (isRangeOrCalendarConfig(config)) return mapRangeFilterConfig(config);

  if (config.optionSource === FilterOptionSource.Dynamic) {
    return mapDynamicDropdownConfig(config);
  }

  return mapStaticDropdownConfig(config);
}

export function mapFilterConfigsToAdditionalFilters(configs: FilterConfig[]): AdditionalFilterInDropdown[] {
  return configs.filter(isConfigMappable).map(mapSingleFilterConfig);
}
